import { markierKlasse, pulsZeit, vorlaufBietetAn, wipHerkunft } from '@netcon-work/shared'
import { useQuery } from '@tanstack/react-query'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react'
import { cn } from '../../lib/cn'
import { heuteSchluessel, useOffeneMarkierungen } from '../../lib/markierung'
import { AufgabenVerweis, useAufgabeOeffnen } from '../AufgabeKontext'
import { Button } from '../ui/primitives'
import { poolsAllerWeltenQuery, sitzungQuery, workspacesQuery } from '../../lib/queries'
import { useMeineAufgaben } from './bestand'
import { useWirksameTermine } from '../../lib/termine'
import {
  MeinTagListe,
  PulsEnde,
  PulsMitte,
  PulsSteuer,
  RadMitte,
  useMarkiertListe,
  usePulsDaten,
  usePulsHandlungen,
  ZellenMenue,
  type CircleAnsicht,
  type CircleFilter,
} from './Puls'
import {
  CIRCLE_RINGE,
  circleLage,
  platzInDerMitte,
  type CircleAufgabe,
  type CircleWelt,
  type Ring,
  type WeltKeil,
} from './circle-lage'

/**
 * **Die Circle-Pipe** ([Z-19](../../../../../docs/phasen/zusatz-19-tagesplan.md) AP 19.9).
 *
 * Winkel = Arbeitswelt (Keilbreite nach WIP-Limit), Radius = Zeitfenster, Mitte = in Arbeit.
 * Die Regel steht in [`circle-lage.ts`](./circle-lage.ts); hier wird nur gezeichnet.
 *
 * ## Zwei Fassungen, keine skalierte
 *
 * **Gemessen am 30.09.2026:** Bei einem Kasten von 1020 Einheiten auf 353 px Anzeigebreite
 * blieben von 11 px Schrift **3,8 px** übrig. Ein Screenshot mit dreifacher Pixeldichte sah gut
 * aus und war es nicht.
 *
 * Mobil deshalb: engerer Kasten (die Weltnamen wandern nach unten in die Kacheln, die ohnehin
 * die Legende sind), doppelte Schrift im SVG und eine doppelte Schwelle fürs Zusammenfassen.
 * Ergebnis **11 px** statt 3,8. Der Preis ist ehrlich: weniger Titel, der Rest als „+N".
 * Fünfzig Titel lesbar in einen Telefonkreis zu bekommen geht nicht.
 */

const FARBEN = ['#2a78d6', '#1baf7a', '#7b5ac4', '#b8752a', '#c2456b', '#2f8f9d']
const LUECKE = 0.008
/**
 * **Die Bandbreiten der fünf Ringe** – von innen nach außen schmaler werdend (55, 49, 44, 40,
 * 36). Innen ist Platz kostbarer: Dort liegt das Dringende, und dort stehen die längsten Titel
 * auf dem kürzesten Bogen.
 */
const BREITEN = [55, 49, 44, 40, 36]

/** Innerer Rand des Rades – zwischen Nabe (116) und erstem Ring bleibt Luft. */
const INNEN = 150

/**
 * **Die Radien für `n` Ringe** (Nils, 30.09.2026: *„leere zeitfenster ausblenden das alles
 * immer an den inneren Kreis rückt"*).
 *
 * Bleiben von fünf Zeitfenstern nur zwei übrig, beginnt der innerste weiterhin bei 150 – die
 * übrigen rücken nach innen nach, statt eine leere Lücke zu lassen. Die Breiten werden dabei so
 * gestreckt, dass das Rad seinen äußeren Umfang behält: Ein Rad, das je nach Bestand mal groß
 * und mal winzig in derselben Fläche läge, wäre bei jedem Poolwechsel ein Sprung.
 *
 * Die **Verhältnisse** bleiben erhalten, nur der Maßstab ändert sich – innen also weiterhin
 * breiter als außen.
 */
function baender(n: number): [number, number][] {
  const genutzt = BREITEN.slice(0, Math.max(1, n))
  const summe = genutzt.reduce((s, b) => s + b, 0)
  const faktor = (AUSSEN - INNEN) / summe
  let r = INNEN
  return genutzt.map((b) => {
    const von = r
    r += b * faktor
    return [von, r] as [number, number]
  })
}

/** Äußerer Rand des Rades – unverändert, damit das Bild nicht je nach Bestand die Größe wechselt. */
const AUSSEN = 374

const MASS = {
  gross: { vb: 1020, vbh: 940, cx: 510, cy: 450, f: 1, namen: true },
  klein: { vb: 792, vbh: 792, cx: 396, cy: 396, f: 2.05, namen: false },
} as const

function mischen(hex: string, hell: number): string {
  const n = Number.parseInt(hex.slice(1), 16)
  const m = (c: number) => Math.round(c + (255 - c) * hell)
  return `rgb(${m((n >> 16) & 255)},${m((n >> 8) & 255)},${m(n & 255)})`
}

/** Dunkel auf hellem Grund, weiß auf dunklem – sonst verschwindet die Schrift stellenweise. */
const schriftfarbe = (hell: number) => (hell > 0.5 ? '#2b2924' : '#ffffff')

/** Der Halbmesser der Nabe. Der innerste Ring beginnt bei 150 – 34 px Luft bleiben. */
const NABE = 116

export function CirclePipe({
  ansicht = 'rad',
  setAnsicht = () => undefined,
  zusatz = null,
}: {
  /** Z-22: was „Mein Tag" in die Kopfzeile des Circle legt – im Puls der nächste Termin. */
  zusatz?: ReactNode
  /** Z-22: Rad oder Fokus-Puls – von „Mein Tag" gehalten, weil dort das Umfeld zurücktritt. */
  ansicht?: CircleAnsicht
  setAnsicht?: (a: CircleAnsicht) => void
} = {}) {
  const { aufgaben, ichId } = useMeineAufgaben()
  const termine = useWirksameTermine()
  const { data: welten = [] } = useQuery(workspacesQuery)
  const { data: sitzung } = useQuery(sitzungQuery)
  const { data: allePools = [] } = useQuery(poolsAllerWeltenQuery)
  const oeffnen = useAufgabeOeffnen()
  const [schmal, setSchmal] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches,
  )

  /**
   * **Beim Drehen des Telefons wechselt die Fassung** – sonst bliebe die unlesbare stehen.
   *
   * `useEffect`, nicht `useMemo`: Ein Merker führt seine Rückgabe nie als Aufräumfunktion aus.
   * Der Horcher bliebe hängen, und bei jedem Neubau käme einer dazu.
   */
  useEffect(() => {
    const frage = window.matchMedia('(max-width: 760px)')
    const horch = () => setSchmal(frage.matches)
    frage.addEventListener('change', horch)
    return () => frage.removeEventListener('change', horch)
  }, [])

  const lage = useMemo(() => {
    const meine: CircleAufgabe[] = aufgaben.map((a) => ({
      id: a.id,
      title: a.title,
      status: a.status,
      zeitfenster: a.zeitfenster ?? null,
      workspaceId: a.workspaceId ?? null,
      groesse: a.groesse ?? null,
      priority: a.priority ?? null,
      dueAt: a.dueAt ?? null,
      updatedAt: a.updatedAt,
    }))
    const wSpalte: CircleWelt[] = welten.map((w, i) => {
      /**
       * **Die Grenze folgt `wipHerkunft`, nicht dem Augenschein** (AP 19.11).
       *
       * Hier stand `w.wipLimit ?? sitzung?.wipLimit ?? 5` – und damit zeigte das Rad für „Meine
       * ToDos" ein Limit von 7, obwohl in einer persönlichen Welt ohne eigenes Limit
       * ausdrücklich **keins** gilt (Nils, 25.08.2026). Die Folge war eine rot gemeldete
       * Überschreitung einer Grenze, die es nach der Regel gar nicht gibt – genau der Befund,
       * der am 30.09. zu zwölf Aufgaben „in Arbeit" geführt hat.
       *
       * **Die Regel bleibt unverändert**, nur die Anzeige sagt jetzt die Wahrheit. Wer für sich
       * eine Grenze will, setzt sie am Workspace – dann greift der erste Zweig.
       */
      const grenze = wipHerkunft({
        personWipLimit: sitzung?.wipLimit ?? 5,
        workspaceWipLimit: w.wipLimit,
        workspaceArt: w.art,
      }).limit
      return {
        id: w.id,
        label: w.label,
        farbe: FARBEN[i % FARBEN.length] ?? '#2a78d6',
        /* Ohne Grenze trotzdem ein Gewicht für die Keilbreite – sonst wäre die Welt unsichtbar. */
        wip: grenze ?? sitzung?.wipLimit ?? 5,
        grenze,
      }
    })
    const jePool = poolsJeAufgabeVon(allePools)
    return circleLage(
      meine
        .filter((a) => a.status === 'in_arbeit' || bietetAn(a, jePool, allePools, ichId))
        /** Z-20: mit wirksamem Termin, damit das Rad sortiert wie Vorlauf und Kalender. */
        .map((a) => ({ ...a, termin: termine.get(a.id) ?? null })),
      wSpalte,
    )
  }, [aufgaben, welten, ichId, sitzung?.wipLimit, allePools, termine])

  /**
   * **Die Anordnung der Ringe** – je Gerät gemerkt, wie jede andere Ansichtsvorliebe.
   * `false` = Zeitfenster (A), `true` = dicht (B). Zum Ausprobieren, bis Nils sich entschieden
   * hat; danach fällt der Umschalter wieder weg oder bleibt, wenn er sich bewährt.
   */

  /**
   * **Die Liste des Laufenden** (Nils, 01.10.2026: *„da müsste man natürlich auch, wenn man auf
   * weitere oder 8 in Arbeit klickt, ein Popup mit denen sehen, sonst komme ich nicht an die,
   * die in Arbeit sind"*).
   *
   * Die Nabe zeigt drei Titel; bei acht laufenden Aufgaben waren fünf davon **nirgends** im Rad
   * erreichbar – im Ring stehen sie nicht (dort liegt der Vorlauf), und der Saum ist zu schmal
   * für Titel. Ein Bild, das die eigene Arbeit zeigt und sie nicht öffnen lässt, ist eine
   * Sackgasse.
   */
  /**
   * **Welche laufenden Aufgaben das Blatt zeigt** – `null` = geschlossen, `'alle'` = über alle
   * Welten, sonst eine Welt-Kennung (Nils, 01.10.2026: *„sonst könnte man da das Popup mit
   * denen, die von dem jeweiligen Workspace in Arbeit sind"*).
   */
  const [inArbeitOffen, setInArbeitOffen] = useState<string | null>(null)

  /* ───────────── Z-22: Fokus-Puls und ☀ Mein Tag ───────────── */

  /** Dieselbe Farbe wie der Keil – Index in `welten`, wie in `wSpalte` oben. */
  const farbeVon = useCallback(
    (weltId: string | null) => {
      const i = welten.findIndex((w) => w.id === weltId)
      return i < 0 ? '#8b8983' : (FARBEN[i % FARBEN.length] ?? '#2a78d6')
    },
    [welten],
  )
  const puls = usePulsDaten(welten, farbeVon)
  const roh = usePulsHandlungen(puls.aufgaben)
  /** Wer etwas in den Puls holt, will ihn sehen; wer ihn beendet, das Rad. */
  const h = {
    ...roh,
    holen: (id: string, dran = false) => {
      roh.holen(id, dran)
      setAnsicht('puls')
    },
    beenden: () => {
      roh.beenden()
      setAnsicht('rad')
    },
  }
  const marken = useOffeneMarkierungen()
  const heute = heuteSchluessel()
  const [filter, setFilter] = useState<CircleFilter>('alle')
  const [menue, setMenue] = useState<{
    weltId: string
    ringId: string
    x: number
    y: number
  } | null>(null)
  const flaeche = useRef<HTMLDivElement>(null)
  const listen = {
    heute: useMarkiertListe('heute', puls.aufgaben),
    morgen: useMarkiertListe('morgen', puls.aufgaben),
    uebermorgen: useMarkiertListe('uebermorgen', puls.aufgaben),
  }
  const { stand } = puls
  const imPuls = (id: string) => stand?.aktivId === id || stand?.ruhendId === id
  const naechstes = listen.heute.find((x) => !imPuls(x.aufgabe.id))?.aufgabe ?? null
  const z = stand ? pulsZeit(stand, puls.jetzt) : null
  const aktiv = stand ? puls.aufgaben.get(stand.aktivId) : undefined
  const zeigtPuls = ansicht === 'puls'

  /**
   * **Läuft ein Puls, zeigt der Circle ihn** – beim Laden und wenn er auf einem anderen Gerät
   * beginnt. Endet er, bleibt die Ansicht stehen: Dort steht dann „Als Nächstes" aus ☀ Heute.
   */
  const hatteStand = useRef<boolean | null>(null)
  useEffect(() => {
    if (!puls.geladen) return
    const jetzt = !!stand
    if (jetzt && hatteStand.current !== true) setAnsicht('puls')
    hatteStand.current = jetzt
  }, [puls.geladen, stand, setAnsicht])

  /**
   * **Zeit um: die Uhr hält an und fragt** – einmal je Block. Der Schlüssel ändert sich mit
   * „+15 min" und mit einer neu gestellten Uhr, dann darf sie wieder fragen.
   */
  const gefragt = useRef(new Set<string>())
  const [endeOffen, setEndeOffen] = useState(false)
  const block = stand ? `${stand.aktivId}|${stand.beginn}|${stand.extraMs}` : null
  const abgelaufen = !!z && z.restMs !== null && z.restMs <= 0 && !z.pausiert
  useEffect(() => {
    if (!abgelaufen || !block || gefragt.current.has(block)) return
    gefragt.current.add(block)
    setEndeOffen(true)
    roh.pausieren()
  }, [abgelaufen, block, roh])

  /** Leertaste = Pause/Weiter, solange der Puls zu sehen ist und kein Feld den Fokus hat. */
  const pauseUmschalten = useRef<() => void>(() => undefined)
  pauseUmschalten.current = () => (stand?.pausiertSeit ? roh.weiter() : roh.pausieren())
  useEffect(() => {
    if (!zeigtPuls || !stand) return
    const taste = (e: KeyboardEvent) => {
      if (e.key !== ' ' || e.repeat) return
      const ziel = e.target as HTMLElement | null
      if (
        ziel?.closest(
          'input, textarea, select, button, a, [contenteditable="true"], [role="dialog"]',
        )
      )
        return
      e.preventDefault()
      pauseUmschalten.current()
    }
    window.addEventListener('keydown', taste)
    return () => window.removeEventListener('keydown', taste)
  }, [zeigtPuls, stand])

  /** Klick auf eine Zelle: ihre Liste an der Stelle öffnen – mit der Tastatur an ihrer Mitte. */
  const zelleWaehlen = useCallback(
    (weltId: string, ringId: string, ziel: Element, punkt?: { x: number; y: number }) => {
      const r = flaeche.current?.getBoundingClientRect()
      if (!r) return
      const b = ziel.getBoundingClientRect()
      const p = punkt ?? { x: b.left + b.width / 2, y: b.top + b.height / 2 }
      setMenue({ weltId, ringId, x: p.x - r.left, y: p.y - r.top })
    },
    [],
  )

  /**
   * **Wie hoch das Rad sein darf** – gemessen, nicht geraten (01.10.2026).
   *
   * Unter dem Rad stehen die Weltleiste und die Fußnote; dafür bleiben 110 px reserviert. Die
   * Untergrenze von 320 px verhindert, dass es auf einem sehr flachen Fenster zur Briefmarke
   * wird – dann darf die Seite lieber scrollen.
   */
  const kasten = useRef<HTMLDivElement>(null)
  const [masse, setMasse] = useState<{ breite: number; hoehe: number }>()
  useEffect(() => {
    const messen = () => {
      const el = kasten.current
      if (!el) return
      /** Was unter dem Rad bis zum Fensterrand bleibt, abzüglich Weltleiste und Fußnote. */
      /* 160 statt 110: seit Z-22 steht über dem Rad die Zeile mit Ansicht und ☀-Filter. */
      const platz = window.innerHeight - el.getBoundingClientRect().top - 160
      /**
       * **Die Breite gewinnt, wenn sie knapp ist.** Auf dem Telefon bleiben unter dem Rad oft nur
       * 300 px – ein Rad von 300 px neben 390 px Platz wäre die schlechteste beider Welten. Dort
       * darf die Seite lieber scrollen; der Boden von 480 px sorgt genau dafür, dass die Breite
       * zum Maß wird, sobald sie kleiner ist als eine brauchbare Höhe.
       *
       * Am Rechner ist die Breite reichlich, also schneidet `Math.min` sie auf die Höhe zu –
       * dort bleibt das Rad vollständig sichtbar, ohne zu scrollen.
       */
      setMasse({ breite: el.clientWidth, hoehe: Math.max(platz, 480) })
    }
    messen()
    window.addEventListener('resize', messen)
    return () => window.removeEventListener('resize', messen)
    /**
     * **Die Zahl der Keile gehört in die Abhängigkeiten** – und das war der Fehler.
     *
     * Beim ersten Zeichnen sind die Aufgaben noch nicht da; dann steht statt des Rades der Satz
     * „Keine Arbeitswelt mit Limit", der Kasten existiert gar nicht, und `messen()` kehrt ohne
     * Ergebnis zurück. Mit `[]` lief es danach **nie wieder** – das Rad blieb beim Notnagel
     * `w-full` und war am Rechner so breit wie die ganze Spalte.
     *
     * Im Gerätemodus nachgemessen: 1128 × 1128 bei 1440×900, obwohl 480 herauskommen sollten.
     * Dass es mobil trotzdem richtig aussah, war Zufall – dort ist die volle Breite die richtige
     * Antwort.
     */
  }, [lage.keile.length])

  const M = schmal ? MASS.klein : MASS.gross

  /**
   * **Der Rahmen folgt dem, was wirklich gezeichnet wird** (Nils, 01.10.2026: *„und den Platz
   * noch besser ausnutzen – denke, der kann noch auf 95 % der Breite, der Kreis, für mobil"*).
   *
   * Seit „Dicht" rückt jeder Keil nach innen auf; wer nur zwei belegte Zeitfenster hat, nutzt
   * von den 374 Radius-Einheiten nur 254. Der Rahmen war aber weiterhin für den vollen Radius
   * bemessen – also stand das Rad klein in der Mitte einer großen leeren Fläche, und auf dem
   * Telefon war das besonders teuer.
   *
   * Jetzt wird der größte **tatsächlich benutzte** Radius gesucht und der Rahmen darum gelegt.
   * Weil `width: 100%` gilt, wächst das Rad damit auf die volle Breite. Der kleine Rand bleibt
   * für die Weltbeschriftung, die am Rechner **außerhalb** des Rades steht.
   */
  const sichtfeld = useMemo(() => {
    const benutzt = Math.max(
      NABE + 30,
      ...lage.keile.map((k) => {
        const belegt = lage.ringe.filter(
          (r) => (k.zellen.find((z) => z.ringId === r.id)?.aufgaben.length ?? 0) > 0,
        ).length
        const band = baender(lage.ringe.length)[Math.max(0, belegt - 1)]
        return belegt === 0 ? NABE + 30 : (band?.[1] ?? AUSSEN)
      }),
    )
    /**
     * **Seitlich Luft für die Weltnamen, oben und unten nicht** (Z-22). Der Rand war ringsum 150
     * – über und unter dem Rad stand deshalb eine leere Fläche, und links reichten 150 trotzdem
     * nicht für „Meine ToDos · 2 offen · 1 in Arbeit". Jetzt ist der Rahmen breiter als hoch.
     */
    const halbX = benutzt + (M.namen ? 190 : 14)
    const halbY = benutzt + 14
    return {
      box: `${M.cx - halbX} ${M.cy - halbY} ${halbX * 2} ${halbY * 2}`,
      halbX,
      halbY,
      benutzt,
    }
  }, [lage, M])
  /** Das Maß in Pixeln: so breit wie möglich, ohne die gemessene Höhe zu überschreiten. */
  const verhaeltnis = sichtfeld.halbX / sichtfeld.halbY
  const breite = masse ? Math.min(masse.breite, masse.hoehe * verhaeltnis) : undefined
  const skala = breite ? breite / (2 * sichtfeld.halbX) : 0
  const stuecke = zeichnen(
    lage.keile,
    lage.ringe,
    M,
    zelleWaehlen,
    setInArbeitOffen,
    marken,
    filter,
    heute,
  )

  if (lage.keile.length === 0) {
    return <p className="p-6 text-center text-[13px] text-muted">Keine Arbeitswelt mit Limit.</p>
  }

  const chip = (aktivChip: boolean, sonnig: boolean) =>
    cn(
      'tabular cursor-pointer rounded-full border px-3 py-[3px] text-[12.5px]',
      aktivChip
        ? sonnig
          ? 'border-sonne-line bg-sonne-bg font-medium text-sonne'
          : 'border-[#bcd5f3] bg-accent-soft font-medium text-accent-deep'
        : 'border-line bg-surface text-ink2 hover:border-line2',
    )

  return (
    <div ref={kasten} className="mx-auto flex flex-col items-center">
      {/**
       * **Kopf: Rad oder Puls, und der ☀-Filter** (Z-22). Läuft ein Puls und man schaut aufs Rad,
       * steht die Uhr hier klein – ein Klick führt zurück.
       */}
      <div className="mb-1 flex w-full flex-wrap items-center justify-center gap-x-3 gap-y-2">
        <div
          role="tablist"
          aria-label="Circle-Ansicht"
          className="inline-flex rounded-full bg-[#ecebe7] p-[3px]"
        >
          {(['rad', 'puls'] as const).map((a) => (
            <button
              key={a}
              type="button"
              role="tab"
              aria-selected={ansicht === a}
              onClick={() => setAnsicht(a)}
              className={cn(
                'cursor-pointer rounded-full px-4 py-1 text-[13px] font-medium',
                ansicht === a ? 'bg-surface text-ink shadow-task' : 'text-ink2 hover:text-ink',
              )}
            >
              {a === 'rad' ? 'Rad' : 'Puls'}
            </button>
          ))}
        </div>
        <div
          className="flex flex-wrap justify-center gap-1.5"
          role="group"
          aria-label="Nach Mein Tag filtern"
        >
          <button
            type="button"
            aria-pressed={filter === 'alle'}
            onClick={() => setFilter('alle')}
            className={chip(filter === 'alle', false)}
          >
            Alle
          </button>
          {(
            [
              ['heute', '☀ Heute'],
              ['morgen', 'Morgen'],
              ['uebermorgen', 'Übermorgen'],
            ] as const
          ).map(([f, text]) => (
            <button
              key={f}
              type="button"
              aria-pressed={filter === f}
              onClick={() => setFilter(filter === f ? 'alle' : f)}
              className={chip(filter === f, true)}
            >
              {text} · {listen[f].length}
            </button>
          ))}
        </div>
        {zusatz}
      </div>
      <div
        ref={flaeche}
        className="relative mx-auto w-full"
        style={
          breite
            ? { width: breite, height: breite / verhaeltnis }
            : { aspectRatio: `${verhaeltnis}` }
        }
      >
        {/**
         * **Das Rad nimmt den Platz, der wirklich da ist** (Nils, 01.10.2026: *„der kann auch noch
         * breiter/höher werden, sodass der eigentlich den sichtbaren Bildschirm immer ausnutzt"*).
         *
         * Vorher war es auf 960 px gedeckelt und richtete sich nur nach der **Breite** – auf einem
         * großen Bildschirm blieb darunter die halbe Seite leer, und auf einem flachen Fenster ragte
         * es unten hinaus.
         *
         * Gemessen wird die Höhe, die unter dem Rad noch bis zum Fensterrand bleibt, abzüglich
         * eines Streifens für die Weltleiste und die Fußnote. **Nicht gerechnet, sondern am
         * gerenderten Kasten abgefragt**: Wie viel über dem Rad steht, hängt an den Terminen des
         * Tages und an der Abwesenheitszeile – eine feste Zahl wäre an jedem zweiten Tag falsch.
         *
         * `aspect-square` mit `max-w-full` hält es quadratisch: Reicht die Breite nicht, folgt die
         * Höhe nach. Auf dem Telefon ist deshalb die Breite das Maß, am Rechner die Höhe.
         */}
        <svg
          viewBox={sichtfeld.box}
          className={cn(
            'mx-auto block size-full transition-opacity duration-500',
            zeigtPuls && 'opacity-[0.38] hover:opacity-60',
          )}
          role="img"
          aria-label="Circle-Pipe: Arbeitswelten als Keile, Zeitfenster als Ringe"
        >
          {stuecke.pfade}
          {stuecke.schrift}
        </svg>
        {/**
         * **Die Mitte im Rad, wie im Mockup** (Z-22): ob der Puls frei ist, wie viel ☀ heute
         * noch liegt, ein Knopf zum Abarbeiten und der Weg zum Laufenden. Die Titel des Laufenden
         * stehen in der Liste dahinter – im Puls wird die Mitte ohnehin zur großen Karte.
         */}
        {!zeigtPuls && skala > 0 && (
          <RadMitte
            durchmesser={2 * (NABE - 2) * skala}
            stand={stand}
            z={z}
            titel={aktiv?.title ?? null}
            heuteOffen={listen.heute.length}
            naechstes={naechstes}
            inArbeit={lage.inArbeitGesamt}
            zumPuls={() => setAnsicht('puls')}
            abarbeiten={() => naechstes && h.holen(naechstes.id)}
            inArbeitZeigen={() => setInArbeitOffen('alle')}
          />
        )}
        {zeigtPuls && (
          <PulsMitte
            durchmesser={
              skala > 0 ? 2 * Math.max(NABE + 40, sichtfeld.benutzt * 0.64) * skala : undefined
            }
            stand={stand}
            aufgaben={puls.aufgaben}
            summen={puls.summen}
            jetzt={puls.jetzt}
            naechstes={naechstes}
            heuteStand={{
              erledigt: puls.erledigtHeute,
              gesamt: listen.heute.length + puls.erledigtHeute,
            }}
            zuRad={() => setAnsicht('rad')}
            h={h}
          />
        )}
        {menue && (
          <ZellenMenue
            kopf={`${lage.keile.find((k) => k.welt.id === menue.weltId)?.welt.label ?? ''} · ${
              lage.ringe.find((r) => r.id === menue.ringId)?.name ?? ''
            }`}
            liste={(
              lage.keile
                .find((k) => k.welt.id === menue.weltId)
                ?.zellen.find((zz) => zz.ringId === menue.ringId)?.aufgaben ?? []
            )
              .map((a) => puls.aufgaben.get(a.id))
              .filter((a): a is NonNullable<typeof a> => !!a)}
            filter={filter}
            x={menue.x}
            y={menue.y}
            stand={stand}
            aufgaben={puls.aufgaben}
            h={h}
            oeffnen={oeffnen}
            zu={() => setMenue(null)}
          />
        )}
      </div>

      {zeigtPuls && stand && z && (
        <PulsSteuer stand={stand} aktiv={aktiv} z={z} h={h} termine={puls.termine} />
      )}
      {h.hinweis && (
        <p role="alert" className="mt-2 max-w-[520px] text-center text-[12.5px] text-bad">
          Nicht gestartet: {h.hinweis}{' '}
          <button type="button" onClick={h.hinweisWeg} className="cursor-pointer underline">
            ok
          </button>
        </p>
      )}
      {endeOffen && stand && aktiv && (
        <PulsEnde
          aktiv={aktiv}
          heuteMs={puls.summen.get(aktiv.id) ?? 0}
          stand={stand}
          h={h}
          zu={() => setEndeOffen(false)}
        />
      )}

      <MeinTagListe
        aufgaben={puls.aufgaben}
        erledigtHeute={puls.erledigtHeute}
        summen={puls.summen}
        stand={stand}
        h={h}
      />

      {inArbeitOffen !== null && (
        <div
          className="unter-leiste fixed inset-x-0 bottom-0 z-[60] flex items-end justify-center bg-[rgb(23_22_20/0.28)] sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-label="In Arbeit"
          onClick={() => setInArbeitOffen(null)}
        >
          <div
            className="flex max-h-[min(70vh,100%)] w-full max-w-[460px] flex-col rounded-t-card border border-line bg-surface shadow-lg sm:rounded-card"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="flex items-center justify-between border-b border-line px-4 py-3 text-[14.5px] font-bold">
              In Arbeit
              {inArbeitOffen !== 'alle' && (
                <span className="ml-2 flex-1 truncate text-[12.5px] font-normal text-ink2">
                  {lage.keile.find((k) => k.welt.id === inArbeitOffen)?.welt.label}
                </span>
              )}
              <span className="text-[12px] font-normal text-muted">
                {lage.keile
                  .filter((k) => inArbeitOffen === 'alle' || k.welt.id === inArbeitOffen)
                  .reduce((n, k) => n + k.inArbeit.length, 0)}{' '}
                Stück
              </span>
            </h3>
            <ul className="min-h-0 flex-1 overflow-y-auto">
              {lage.keile
                .filter((k) => inArbeitOffen === 'alle' || k.welt.id === inArbeitOffen)
                .flatMap((k) =>
                  k.inArbeit.map((a) => (
                    <li key={a.id} className="border-b border-line last:border-0">
                      {/*
                       * **Beim Klick schließt das Blatt** (Nils, 01.10.2026: *„beim Klick auf
                       * eine Aufgabe in dem Popup fällt noch auf, dass die Aufgabe dann im
                       * Hintergrund sich öffnet"*). Das Aufgabendetail ist selbst eine Ebene über
                       * der Seite; zwei übereinander heißt, dass die untere die obere verdeckt.
                       */}
                      <AufgabenVerweis
                        aufgabeId={a.id}
                        onClick={() => setInArbeitOffen(null)}
                        className="flex w-full items-center gap-2.5 px-4 py-2 text-left hover:bg-[#fafaf8]"
                      >
                        <span
                          aria-hidden="true"
                          className="size-2.5 shrink-0 rounded-[3px]"
                          style={{ background: k.welt.farbe ?? '#8b8983' }}
                        />
                        <span className="min-w-0 flex-1">
                          <b className="block truncate text-[13px] font-medium">{a.title}</b>
                          <span className="text-[11.5px] text-muted">{k.welt.label}</span>
                        </span>
                      </AufgabenVerweis>
                    </li>
                  )),
                )}
            </ul>
            <footer className="flex justify-end border-t border-line px-4 py-2.5">
              <Button variant="ghost" onClick={() => setInArbeitOffen(null)}>
                Schließen
              </Button>
            </footer>
          </div>
        </div>
      )}

      <div className={cn('mt-3 flex flex-wrap justify-center gap-2', zeigtPuls && 'opacity-50')}>
        {lage.keile.map((k) => (
          <span
            key={k.welt.id}
            className="flex items-center gap-2 rounded-[10px] border border-line bg-surface px-2.5 py-1.5 text-[12px]"
          >
            <i
              className="block h-[9px] w-[9px] rounded-[3px]"
              style={{ background: k.welt.farbe ?? '#8b8983' }}
            />
            <b className="font-medium">{k.welt.label}</b>
            <span className={k.ueber ? 'font-semibold text-bad' : 'text-muted'}>
              {k.welt.grenze === null
                ? `${k.inArbeit.length} · ohne Grenze`
                : `${k.inArbeit.length} / ${k.welt.grenze}`}
            </span>
          </span>
        ))}
      </div>

      <p className="mt-1.5 text-center text-[12px] text-muted">
        <b className="font-medium text-ink2">Keilbreite = WIP-Limit der Welt.</b> Ringe von innen
        nach außen: jetzt · 5 Tage · 10 Tage · Monat · offen. In die Mitte kommt nur, wofür Platz
        ist. <span className="text-sonne">●</span> = ☀ heute markiert. Im Puls: höchstens zwei
        Dinge, eins ist dran.
      </p>
    </div>
  )
}

type Masse = (typeof MASS)['gross'] | (typeof MASS)['klein']

/**
 * **Das Rad zeichnen.**
 *
 * Getrennt von der Komponente, weil hier nur Geometrie steht – und weil eine Funktion, die
 * 200 SVG-Knoten baut, mitten in einer Komponente niemand mehr liest.
 *
 * ## Eine Zelle je Welt und Zeitfenster, mit Zahl (Z-22, 19.10.2026)
 *
 * Bis hierher stand jede Aufgabe als eigenes Feld mit gekrümmtem Titel im Ring – bei 11 px auf
 * einem Bogen, und in vollen Zellen als „+N". Nils hat das Mockup des Fokus-Pulses abgenommen und
 * danach gesagt: *„sieht aber optisch noch nicht aus wie in deinem mockup"*. Dort ist jede Zelle
 * eine Fläche mit **einer Zahl**; die Titel stehen in der Liste, die ein Klick auf die Zelle
 * öffnet – lesbar, mit „In den Puls", „Öffnen" und ☀. Das Rad zeigt die Lage, die Liste die Dinge.
 */
function zeichnen(
  keile: WeltKeil[],
  ringe: Ring[],
  M: Masse,
  zelleWaehlen: (
    weltId: string,
    ringId: string,
    ziel: Element,
    punkt?: { x: number; y: number },
  ) => void,
  weltWaehlen: (weltId: string) => void,
  /** Z-22: offene Markierungen `taskId → Tag`, der Filter darüber und „heute". */
  marken: ReadonlyMap<string, string>,
  filter: CircleFilter,
  heute: string,
) {
  const BAND = baender(ringe.length)
  const pfade: ReactElement[] = []
  const schrift: ReactElement[] = []
  const pol = (r: number, w: number): [number, number] => [
    M.cx + r * Math.cos(w),
    M.cy + r * Math.sin(w),
  ]
  const sektor = (r1: number, r2: number, a1: number, a2: number) => {
    const [x1, y1] = pol(r2, a1)
    const [x2, y2] = pol(r2, a2)
    const [x3, y3] = pol(r1, a2)
    const [x4, y4] = pol(r1, a1)
    const gross = a2 - a1 > Math.PI ? 1 : 0
    return `M${x1},${y1} A${r2},${r2} 0 ${gross} 1 ${x2},${y2} L${x3},${y3} A${r1},${r1} 0 ${gross} 0 ${x4},${y4} Z`
  }
  const stufe = (id: string) => {
    const am = marken.get(id)
    return am ? markierKlasse(am, heute) : null
  }

  let lauf = -Math.PI / 2
  for (const k of keile) {
    const spanne = 2 * Math.PI * k.anteil
    const a1 = lauf + LUECKE
    const a2 = lauf + spanne - LUECKE
    const farbe = k.welt.farbe ?? '#8b8983'

    /**
     * **„Dicht"** (Nils, 01.10.2026): Jeder Keil rückt seine belegten Fenster nach innen auf, statt
     * leere Bögen zu lassen. Die Reihenfolge bleibt: dringend innen, fern außen.
     */
    const eigene = ringe.filter(
      (r) => (k.zellen.find((z) => z.ringId === r.id)?.aufgaben.length ?? 0) > 0,
    )
    eigene.forEach((ring, i) => {
      const liste = k.zellen.find((z) => z.ringId === ring.id)?.aufgaben ?? []
      const [r1, r2] = BAND[i]!
      /**
       * **Innen kräftig, außen blass** – nach dem festen Platz des Zeitfensters in `CIRCLE_RINGE`,
       * nicht nach dem Ring im Keil: in „Dicht" rückt ein Keil auf, und „offen" bliebe sonst in
       * einer Welt mit zwei Fenstern so kräftig wie „5 Tage". Vorher richtete sich die Farbe nach
       * der Anzahl – der volle Ring „offen" war der dunkelste und zog den Blick nach außen.
       */
      const fenster = Math.max(
        0,
        CIRCLE_RINGE.findIndex((r) => r.id === ring.id),
      )
      const hell = 0.3 + Math.min(fenster, 4) * 0.13
      /** Der Filter dämpft, statt zu verstecken – das Rad bleibt dasselbe Bild. */
      const treffer =
        filter === 'alle' ? liste.length : liste.filter((a) => stufe(a.id) === filter).length
      const gedaempft = filter !== 'alle' && treffer === 0
      const name = `${k.welt.label} · ${ring.name}: ${liste.length} ${liste.length === 1 ? 'Aufgabe' : 'Aufgaben'}`
      const schluessel = `${k.welt.id}-${ring.id}`
      pfade.push(
        <path
          key={`z${schluessel}`}
          d={sektor(r1 + 1.5, r2 - 1.5, a1, a2)}
          fill={mischen(farbe, gedaempft ? 0.93 : hell)}
          role="button"
          tabIndex={0}
          aria-label={name}
          className="cursor-pointer outline-none hover:brightness-95 focus-visible:brightness-90"
          onClick={(e) =>
            zelleWaehlen(k.welt.id, ring.id, e.currentTarget, { x: e.clientX, y: e.clientY })
          }
          onKeyDown={(e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return
            e.preventDefault()
            zelleWaehlen(k.welt.id, ring.id, e.currentTarget)
          }}
        >
          <title>{name}</title>
        </path>,
      )
      const mitte = (a1 + a2) / 2
      const rm = (r1 + r2) / 2
      if (treffer > 0 && rm * (a2 - a1) > 20 * M.f) {
        const [x, y] = pol(rm, mitte)
        schrift.push(
          <text
            key={`n${schluessel}`}
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={16 * M.f}
            fontWeight={650}
            fill={gedaempft ? '#b9b6ae' : schriftfarbe(hell)}
            className="pointer-events-none"
          >
            {treffer}
          </text>,
        )
      }
      /**
       * **☀ Für heute markiert** – ein Bernsteinpunkt mit Zahl am Anfang der Zelle. Er bleibt auch
       * im Puls sichtbar, wenn das Rad zurücktritt: als Wegweiser, wo der Tag noch liegt.
       */
      const sonnen = liste.filter((a) => stufe(a.id) === 'heute').length
      if (sonnen > 0 && (filter === 'alle' || filter === 'heute')) {
        const rr = Math.min(11 * M.f, (r2 - r1) / 2.6)
        const rs = r1 + rr + 4 * M.f
        const [sx, sy] = pol(rs, a1 + (rr + 6 * M.f) / rs)
        schrift.push(
          <g key={`s${schluessel}`} className="pointer-events-none">
            <circle cx={sx} cy={sy} r={rr} fill="#f5b400" stroke="#fff" strokeWidth={2 * M.f} />
            {rr >= 7 * M.f && (
              <text
                x={sx}
                y={sy}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={12 * M.f}
                fontWeight={700}
                fill="#4a3500"
              >
                {sonnen}
              </text>
            )}
          </g>,
        )
      }
    })

    /**
     * **Der Saum ist der Zugang zum Laufenden dieser Welt** (Nils, 01.10.2026): Er zeigt mit der
     * Weltfarbe, woher das Laufende kommt; ein Klick öffnet die Liste dieser Welt.
     */
    const laufendeDieserWelt = k.inArbeit.length
    pfade.push(
      <g
        key={`m${k.welt.id}`}
        className={laufendeDieserWelt > 0 ? 'cursor-pointer' : ''}
        onClick={laufendeDieserWelt > 0 ? () => weltWaehlen(k.welt.id) : undefined}
      >
        <path
          d={sektor(NABE + 4, NABE + 30, a1, a2)}
          fill={mischen(farbe, laufendeDieserWelt === 0 ? 0.95 : k.ueber ? 0.3 : 0.5)}
          className={laufendeDieserWelt > 0 ? 'hover:brightness-95' : ''}
        >
          <title>
            {k.welt.label}
            {k.welt.grenze === null
              ? ` · ${laufendeDieserWelt} in Arbeit`
              : ` · ${laufendeDieserWelt} / ${k.welt.grenze} in Arbeit`}
            {laufendeDieserWelt > 0 ? ' – anklicken für die Liste' : ''}
          </title>
        </path>
        {laufendeDieserWelt > 0 && (NABE + 17) * (a2 - a1) > 26 && (
          <text
            x={pol(NABE + 17, (a1 + a2) / 2)[0]}
            y={pol(NABE + 17, (a1 + a2) / 2)[1] + 4}
            textAnchor="middle"
            fontSize={12 * M.f}
            fontWeight={600}
            fill={schriftfarbe(k.ueber ? 0.3 : 0.5)}
            className="pointer-events-none"
          >
            {laufendeDieserWelt}
          </text>
        )}
      </g>,
    )

    /** Am Rechner der Weltname außen – mit dem, was offen ist und was läuft (wie im Mockup). */
    if (M.namen) {
      const [lx, ly] = pol(AUSSEN + 18, (a1 + a2) / 2)
      const anker = lx < M.cx - 14 ? 'end' : lx > M.cx + 14 ? 'start' : 'middle'
      const offen = k.zellen.reduce((n, z) => n + z.aufgaben.length, 0)
      schrift.push(
        <g key={`w${k.welt.id}`} className="pointer-events-none">
          <text x={lx} y={ly} fontSize={15} fontWeight={600} fill={farbe} textAnchor={anker}>
            {k.welt.label}
          </text>
          <text
            x={lx}
            y={ly + 17}
            fontSize={12}
            fontWeight={500}
            fill={k.ueber ? '#d03b3b' : '#8b8983'}
            textAnchor={anker}
          >
            {offen} offen ·{' '}
            {k.welt.grenze === null
              ? `${k.inArbeit.length} in Arbeit`
              : `${k.inArbeit.length} / ${k.welt.grenze} in Arbeit`}
          </text>
        </g>,
      )
    }

    /**
     * **Keine Trennlinie mehr** (Z-22): Sie lief von der Mitte bis zum Rand und schnitt das Rad bei
     * zwei Welten in zwei Hälften. Die Lücke zwischen den Keilen (`LUECKE`) trennt genug.
     */
    lauf += spanne
  }

  return { pfade, schrift }
}

export { platzInDerMitte }

/**
 * **Bietet der Vorlauf diese Aufgabe an?** – dieselbe Regel wie in `lage.ts`
 * ([E-394](../../../../../docs/ENTSCHEIDUNGEN.md#e-394), 01.10.2026).
 *
 * **Eine Abweichung, benannt:** `hatTermin` wird hier aus Fälligkeit und Zeitfenster abgeleitet,
 * während der Vorlauf zusätzlich Meilensteine und Vorhaben-Zieltermine einrechnet. Am
 * 01.10.2026 nachgemessen: **0 von 0** – keine Aufgabe in einem „nur mit Termin"-Pool hängt
 * ihren Termin an einem Vorhaben. Sollte das je vorkommen, erschiene sie hier als „ohne
 * Termin" und im Vorlauf als „mit" – dann gehört die Ableitung in den Bestand statt in jede
 * Ansicht.
 */
function bietetAn(
  a: { dueAt?: string | null; zeitfenster?: string | null; id: string },
  poolsJeAufgabe: ReadonlyMap<string, string[]>,
  pools: readonly { id: string; vorlaufNurMitTermin: boolean }[],
  ichId: string | null,
): boolean {
  return vorlaufBietetAn(
    {
      hatTermin: !!a.dueAt || !!a.zeitfenster,
      poolIds: poolsJeAufgabe.get(a.id) ?? [],
    },
    pools,
    ichId,
  )
}

/** Aufgabe → Pöple, aus der Pool-Abfrage umgehängt. `tasks.all` führt sie nicht mit. */
function poolsJeAufgabeVon(
  pools: readonly { id: string; aufgaben?: readonly { id: string }[] }[],
): Map<string, string[]> {
  const karte = new Map<string, string[]>()
  for (const pool of pools) {
    for (const a of pool.aufgaben ?? []) {
      const vorhanden = karte.get(a.id)
      if (vorhanden) vorhanden.push(pool.id)
      else karte.set(a.id, [pool.id])
    }
  }
  return karte
}
